import { THEME_VARIABLE } from '@/config';
import { OrderStatusTab, PickupStatus, RemittanceStatus, SubTabOrderKey } from '@/types';
import translations from '@/i18n/en/translation.json';
import { ORDER_STATUE_GROUPED_BY_TAB, OrderStatusEnum } from '@/config/constants';

export function getOrderStatusTagData(status?: OrderStatusEnum) {
  const statusTagData = {
    [OrderStatusTab.READY_TO_SHIP]: {
      iconColor: THEME_VARIABLE?.customDarkBlue,
      tagColor: 'purple',
    },
    [OrderStatusTab.IN_PROGRESS]: { iconColor: THEME_VARIABLE?.customBlue, tagColor: 'processing' },
    [OrderStatusTab.ON_HOLD]: { iconColor: THEME_VARIABLE?.customOrange, tagColor: 'warning' },
    [OrderStatusTab.RTO]: { iconColor: THEME_VARIABLE?.colorPrimary, tagColor: 'yellow' },
    [OrderStatusTab.INVENTORY]: { iconColor: THEME_VARIABLE?.customBlue, tagColor: 'processing' },
    [OrderStatusTab.FINISHED]: { iconColor: THEME_VARIABLE?.customGreen, tagColor: 'success' },
  };

  const orderTab = getOrderTabByStatus(status);
  if (orderTab)
    return {
      key: orderTab,
      ...statusTagData[orderTab],
    };

  return { key: 'cancelled', iconColor: THEME_VARIABLE?.customRed, tagColor: 'error' };
}

export function getPickupStatusTagData(status?: PickupStatus) {
  const statusTagData = {
    [PickupStatus.BULK_COLLECTED]: {
      iconColor: THEME_VARIABLE?.customDarkBlue,
      tagColor: 'purple',
    },
    [PickupStatus.OUT_FOR_COLLECTION]: {
      iconColor: THEME_VARIABLE?.customBlue,
      tagColor: 'processing',
    },
    [PickupStatus.CANCELLED]: { iconColor: THEME_VARIABLE?.customRed, tagColor: 'error' },
    [PickupStatus.NEW]: { iconColor: THEME_VARIABLE?.customGreen, tagColor: 'success' },
    [PickupStatus.COLLECTED]: {
      iconColor: THEME_VARIABLE?.colorPrimary,
      tagColor: 'yellow',
    },
    // [RemittanceStatus.CANCELLED]: { iconColor: THEME_VARIABLE?.customOrange, tagColor: 'warning' },
  };
  if (status && statusTagData[status])
    return {
      key: status,
      ...statusTagData[status],
    };

  return {
    key: RemittanceStatus.CANCELLED,
    iconColor: THEME_VARIABLE?.customRed,
    tagColor: 'error',
  };
}

// //bulk-collected
export function getRemittanceStatusTagData(status?: RemittanceStatus) {
  const statusTagData = {
    [RemittanceStatus.INITIATED]: {
      iconColor: THEME_VARIABLE?.customDarkBlue,
      tagColor: 'purple',
    },
    [RemittanceStatus.REQUESTED]: { iconColor: THEME_VARIABLE?.customBlue, tagColor: 'processing' },
    [RemittanceStatus.CANCELLED]: { iconColor: THEME_VARIABLE?.customRed, tagColor: 'error' },
    [RemittanceStatus.DELIVERED]: { iconColor: THEME_VARIABLE?.customGreen, tagColor: 'success' },
    [RemittanceStatus.READY_TO_PICKUP]: {
      iconColor: THEME_VARIABLE?.colorPrimary,
      tagColor: 'yellow',
    },
    // [RemittanceStatus.CANCELLED]: { iconColor: THEME_VARIABLE?.customOrange, tagColor: 'warning' },
  };
  if (status && statusTagData[status])
    return {
      key: status,
      ...statusTagData[status],
    };

  return {
    key: RemittanceStatus.CANCELLED,
    iconColor: THEME_VARIABLE?.customRed,
    tagColor: 'error',
  };
}

export const getOrderTabByStatus = (statusSlug?: OrderStatusEnum) => {
  if (statusSlug)
    for (const key in ORDER_STATUE_GROUPED_BY_TAB) {
      if (ORDER_STATUE_GROUPED_BY_TAB[key as OrderStatusTab].includes(statusSlug)) {
        return key as OrderStatusTab;
      }
    }
  return undefined;
};

export function getStatusActivePickup(active: string): number {
  switch (active) {
    case 'new':
      return -1;
    case 'scheduled':
      return 0;
    case 'collecting':
      return 1;
    case 'out-for-collection':
      return 1;
    case 'out-for-collecting':
      return 1;
    case 'collected':
      return 2;
    default:
      return 0;
  }
}

export function getStatusActiveOrderRTO(active: string): number {
  switch (active) {
    case 'rto-scheduled':
      return 0;
    case 'rto-in-transit':
      return 1;
    case 'rto-delivered':
      return 2;
    default:
      return -1;
  }
}

export function mapOrderStatusToValue(status: OrderStatusEnum): string | null {
  const statusInfo = getOrderStatusTagData(status);

  if (!statusInfo?.key) {
    return null;
  }

  switch (statusInfo.key) {
    case OrderStatusTab.READY_TO_SHIP:
      return 'created';
    case OrderStatusTab.IN_PROGRESS:
      return 'in_progress';
    case OrderStatusTab.ON_HOLD:
      return 'on_hold';
    case OrderStatusTab.FINISHED:
      return 'delivered';
    default:
      return 'created';
  }
}

export interface listItem {
  title: string;
  value: string;
  badgeKey: SubTabOrderKey;
}

//on_hold???
export const listStatusButtons: { [key in OrderStatusTab]: listItem[] } = {
  [OrderStatusTab.READY_TO_SHIP]: [
    { title: 'merchant_title_order_status.new', value: 'new', badgeKey: 'new' },
    { title: 'merchant_title_order_status.scheduled', value: 'scheduled', badgeKey: 'scheduled' },
  ],
  [OrderStatusTab.IN_PROGRESS]: [
    {
      title: 'merchant_title_order_status.collecting',
      value: 'collecting',
      badgeKey: 'collecting',
    },
    { title: 'merchant_title_order_status.collected', value: 'collected', badgeKey: 'collected' },
    { title: 'merchant_title_order_status.en-route', value: 'en-route', badgeKey: 'in_transit' },
    {
      title: 'merchant_title_order_status.out for delivery',
      value: 'out-for-delivery',
      badgeKey: 'out_for_delivery',
    },
    { title: 'merchant_title_order_status.postponed', value: 'postponed', badgeKey: 'postponed' },
  ],
  [OrderStatusTab.RTO]: [
    {
      title: 'merchant_title_order_status.rto-requested',
      value: 'rto-requested',
      badgeKey: 'rto_requested',
    },
    {
      title: 'merchant_title_order_status.rto-scheduled',
      value: 'rto-scheduled',
      badgeKey: 'rto_scheduled',
    },
    {
      title: 'merchant_title_order_status.returned - warehouse',
      value: 'rto-received-warehouse',
      badgeKey: 'returned_warehouse',
    },
    {
      title: 'merchant_title_order_status.returning-origin',
      value: 'rto-in-transit',
      badgeKey: 'returning_origin',
    },
  ],
  [OrderStatusTab.FINISHED]: [
    { title: 'merchant_title_order_status.delivered', value: 'delivered', badgeKey: 'delivered' },
    {
      title: 'merchant_title_order_status.partially-returned',
      value: 'rto-partially-delivered',
      badgeKey: 'partially_returned',
    },
    { title: 'merchant_title_order_status.returned', value: 'rto-delivered', badgeKey: 'returned' },
    {
      title: 'merchant_title_order_status.partially-delivered',
      value: 'partially-delivered',
      badgeKey: 'partially_delivered',
    },
    { title: 'merchant_title_order_status.resend', value: 'resend', badgeKey: 'resend' },
  ],
  [OrderStatusTab.ON_HOLD]: [
    { title: 'merchant_title_order_status.on-hold', value: 'on-hold', badgeKey: 'on_hold' },
    { title: 'merchant_title_order_status.exception', value: 'exception', badgeKey: 'exception' },
  ],
  [OrderStatusTab.INVENTORY]: [],
};

export enum TypeTransition {
  deposit = 'deposit',
  withdraw = 'withdraw',
}

export const groupStatusesByMerchantTitle = () => {
  const grouped: Record<string, string[]> = {};
  const subCategories: Record<string, string> = translations.merchant_order_status;
  Object.entries(subCategories).forEach(([key, value]) => {
    if (!grouped[value]) {
      grouped[value] = [];
    }
    grouped[value].push(key);
  });

  return grouped;
};
